import { CSSProperties, useLayoutEffect, useRef, useState } from 'react';

/**
 * Share of the container width the text travels per second. Keeping the speed relative to the
 * container instead of using a fixed pixel rate makes the scroll look the same on any TV resolution.
 */
const WIDTHS_PER_SECOND = 0.08;

const LINES_PER_SECOND = 0.5;

/** Share of the animation spent moving — the rest is the pause at each end. Must match the keyframes. */
export const TRAVEL_SHARE = 0.35;

/** Sub-pixel differences between the text and its container are rounding noise, not real overflow. */
const OVERFLOW_THRESHOLD = 1;

export type ScrollAxis = 'horizontal' | 'vertical';

export interface OverflowScroll {
  distance: number;
  duration: number;
}

export const getOverflowScrollStyle = (scroll: OverflowScroll | null): CSSProperties | undefined =>
  scroll
    ? ({ '--scroll-distance': `${scroll.distance}px`, animationDuration: `${scroll.duration}s` } as CSSProperties)
    : undefined;

interface Measurement {
  containerSize: number;
  contentSize: number;
  unit: number;
  unitsPerSecond: number;
}

type MeasureAlongAxis = (container: HTMLElement, textElement: HTMLElement) => Measurement;

const measureHorizontal: MeasureAlongAxis = (container, textElement) => ({
  containerSize: container.clientWidth,
  contentSize: textElement.scrollWidth,
  unit: container.clientWidth,
  unitsPerSecond: WIDTHS_PER_SECOND,
});

const measureVertical: MeasureAlongAxis = (container, textElement) => {
  const containerSize = container.getBoundingClientRect().height;
  const lineHeight = Number.parseFloat(window.getComputedStyle(textElement).lineHeight);

  return {
    containerSize,
    contentSize: textElement.getBoundingClientRect().height,
    unit: lineHeight || containerSize,
    unitsPerSecond: LINES_PER_SECOND,
  };
};

const MEASURE_BY_AXIS: Record<ScrollAxis, MeasureAlongAxis> = {
  horizontal: measureHorizontal,
  vertical: measureVertical,
};

/**
 * Measures whether `text` overflows its container and, if it does, returns how far it has to scroll
 * and how long that should take. Returns `null` while the text fits, so the caller can fall back to
 * a static, ellipsised label.
 */
export const useOverflowScroll = (text: string | undefined, axis: ScrollAxis = 'horizontal') => {
  const containerRef = useRef<HTMLDivElement>(null);
  const textRef = useRef<HTMLSpanElement>(null);
  const [scroll, setScroll] = useState<OverflowScroll | null>(null);

  useLayoutEffect(() => {
    const container = containerRef.current;
    const textElement = textRef.current;

    if (!container || !textElement) {
      return;
    }

    const measure = () => {
      const { containerSize, contentSize, unit, unitsPerSecond } = MEASURE_BY_AXIS[axis](container, textElement);
      const overflow = contentSize - containerSize;

      if (containerSize === 0 || overflow <= OVERFLOW_THRESHOLD) {
        setScroll(null);
        return;
      }

      const travelSeconds = overflow / unit / unitsPerSecond;
      setScroll({ distance: overflow, duration: travelSeconds / TRAVEL_SHARE });
    };

    measure();

    // Font sizes are viewport based, so the text has to be re-measured whenever the widget resizes.
    if (typeof ResizeObserver === 'undefined') {
      return;
    }

    const observer = new ResizeObserver(measure);
    observer.observe(container);
    observer.observe(textElement);

    return () => observer.disconnect();
  }, [text, axis]);

  return { containerRef, textRef, scroll };
};
